<template>
  <div v-if="displayBanner" class="ad-banner" @click="handleClick">
    <div
      class="banner-bg"
      :style="{ backgroundImage: `linear-gradient(105deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%), url('${displayBanner.imageUrl}')` }"
    ></div>

    <div class="banner-content">
      <div class="banner-badge">{{ displayBanner.badge || 'AD' }}</div>

      <h2 class="banner-title" v-html="formatTitle(displayBanner.title)"></h2>

      <div class="banner-footer">
        <span class="banner-cta">{{ displayBanner.ctaText || '자세히 보기' }}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAdBanner } from '@/composables/main/useAdBanner'

export default {
  name: 'AdBanner',
  props: {
    // 외부에서 데이터를 넣어줄 때 (예: 관리자 미리보기)
    banner: {
      type: Object,
      default: null
    }
  },
  emits: ['click'],
  setup(props, { emit }) {
    const router = useRouter()
    const { getRandomBanner, incrementClick, incrementImpression } = useAdBanner()

    // 내부에서 스스로 불러올 때 담을 변수
    const internalBanner = ref(null)

    // 최종적으로 보여줄 배너 결정 (props가 우선, 없으면 내부 데이터)
    const displayBanner = computed(() => {
      return props.banner || internalBanner.value
    })

    // 배너 데이터 가져오기
    const fetchRandomBanner = async () => {
      const banner = await getRandomBanner()
      internalBanner.value = banner

      // 노출 수 증가 (실제 배너만)
      if (banner?.id) {
        incrementImpression(banner.id)
      }
    }

    // 제목 포맷팅 (줄바꿈 처리)
    const formatTitle = (text) => {
      if (!text) return ''
      return text.replace(/\n/g, '<br/>')
    }

    // 클릭 핸들러
    const handleClick = () => {
      const target = displayBanner.value
      if (!target) return

      // 1. 통계 집계 (실제 ID가 있는 경우에만)
      if (target.id) {
        incrementClick(target.id)
      }

      // 2. 부모에게 클릭 알림
      emit('click', target)

      // 3. 링크 이동 (props로 받은 경우=미리보기 때는 이동 안 함, 실제 사용 시에만 이동)
      if (!props.banner && target.linkUrl) {
        // 파트너 광고는 from=adbanner 쿼리 파라미터 추가
        if (target.linkUrl.startsWith('http')) {
          window.open(target.linkUrl, '_blank')
        } else {
          // URL에 이미 쿼리 파라미터가 있으면 &, 없으면 ? 사용
          const separator = target.linkUrl.includes('?') ? '&' : '?'
          router.push(`${target.linkUrl}${separator}from=adbanner`)
        }
      }
    }

    onMounted(async () => {
      // props로 받은 데이터가 없을 때만 스스로 데이터를 가져옴
      if (!props.banner) {
        await fetchRandomBanner()
      }
    })

    // props.banner가 변경되면 internalBanner 초기화
    watch(() => props.banner, (newVal) => {
      if (newVal) {
        internalBanner.value = null
      }
    })

    return {
      displayBanner,
      formatTitle,
      handleClick
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.ad-banner {
  @apply relative w-full rounded-2xl overflow-hidden cursor-pointer shadow-lg;
  height: 160px;
  background-color: #333;
  transition: transform 0.2s ease;
}

.ad-banner:active {
  transform: scale(0.98);
}

.banner-bg {
  @apply absolute inset-0;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
  z-index: 1;
}

.ad-banner:hover .banner-bg {
  transform: scale(1.05);
}

.banner-content {
  @apply relative h-full p-6 flex flex-col justify-between items-start text-white;
  z-index: 2;
}

.banner-badge {
  @apply px-2 py-1 text-xs font-bold rounded-md;
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  letter-spacing: 0.5px;
}

.banner-title {
  @apply text-xl font-extrabold mt-2;
  line-height: 1.35;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
}

.banner-footer {
  @apply flex items-center gap-1 mt-auto;
  opacity: 0.9;
}

.banner-cta {
  @apply text-sm font-semibold;
}
</style>
